import { useEffect, useState } from 'react';

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useBulkUpdateProductsStock } from '../../hooks';
import { STOCK_STATUS_OPTIONS } from '../../model/options';
import type { ProductSearchRequest, StockStatus } from '../../model/types';
import type { ToastSeverity } from '../../lib/useToast';

interface BulkStockUpdateDialogProps {
  open: boolean;
  selectedCount: number;
  totalCount: number;
  selectAllMode: boolean;
  selectedIds: Set<string>;
  searchRequest: ProductSearchRequest;
  onClose: () => void;
  onResult: (severity: ToastSeverity, message: string) => void;
}

function toNumberOrUndefined(value: string): number | undefined {
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  const n = Number(trimmed);
  return Number.isNaN(n) ? undefined : n;
}

/** Bulk stock status update dialog for selected products in the search results. */
export function BulkStockUpdateDialog({
  open,
  selectedCount,
  totalCount,
  selectAllMode,
  selectedIds,
  searchRequest,
  onClose,
  onResult,
}: BulkStockUpdateDialogProps) {
  const update = useBulkUpdateProductsStock();
  const [stockStatus, setStockStatus] = useState<StockStatus>('IN_STOCK');
  const [quantity, setQuantity] = useState('');

  useEffect(() => {
    if (open) {
      setStockStatus('IN_STOCK');
      setQuantity('');
    }
  }, [open]);

  const submit = () => {
    if (selectedCount === 0) return;
    update.mutate(
      {
        productIds: selectAllMode ? undefined : Array.from(selectedIds),
        searchRequest: selectAllMode ? searchRequest : undefined,
        stockStatus,
        stockQuantity: toNumberOrUndefined(quantity),
      },
      {
        onSuccess: (result) => {
          onResult(
            'success',
            `Обновлено товаров: ${result.updatedCount}${result.failedCount > 0 ? ` (ошибок: ${result.failedCount})` : ''}`,
          );
          onClose();
        },
        onError: (error) => onResult('error', error.message),
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Обновить наличие для выбранных товаров</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {selectAllMode
              ? `Будут обновлены все ${totalCount} товаров в результатах поиска`
              : `Выбрано товаров: ${selectedCount}`}
          </Typography>
          <TextField
            select
            label="Наличие"
            value={stockStatus}
            onChange={(e) => setStockStatus(e.target.value as StockStatus)}
            fullWidth
          >
            {STOCK_STATUS_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Количество на складе"
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            helperText="Необязательно — оставьте пустым, чтобы не менять."
            fullWidth
            slotProps={{ htmlInput: { min: 0, step: 1 } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={update.isPending}>
          Отмена
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={update.isPending || selectedCount === 0}
          startIcon={update.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );
}
